// Project discussion (Chat tab) UI state. All conversation/message data lives
// in the `chat` module — this module keeps only the map-interaction state
// (pin-drop mode, drafts, the active on-map thread) and delegates the rest,
// preserving the getter/action surface the components already use.
export default {
  namespaced: true,
  state: () => ({
    enabledByProject: {}, // String(projectId) -> boolean; absent = enabled
    // "click the map to place" mode: null | 'composer' (location goes to the
    // chat composer) | 'map' (opens the on-map compose bubble)
    pinDrop: null,
    draftLocation: null, // [lng, lat] captured for the chat composer
    mapDraft: null,      // [lng, lat] where the on-map compose bubble is open
    mapDraftGeometry: null, // GeoJSON drawn for the pending on-map message
    activeThreadId: null, // message currently open in the map popup
  }),
  getters: {
    messages: (state, getters, rootState, rootGetters) => (projectId) => {
      const uuid = rootGetters['chat/projectUuid'](projectId)
      return uuid ? rootGetters['chat/threads'](uuid) : []
    },
    pendingAgent: (state, getters, rootState, rootGetters) => (projectId) => {
      const uuid = rootGetters['chat/projectUuid'](projectId)
      return uuid ? rootGetters['chat/pendingAgent'](uuid) : null
    },
    isEnabled: (state) => (projectId) => state.enabledByProject[String(projectId)] !== false,
    pinDrop: (state) => state.pinDrop,
    draftLocation: (state) => state.draftLocation,
    mapDraft: (state) => state.mapDraft,
    mapDraftGeometry: (state) => state.mapDraftGeometry,
    activeThread: (state, getters) => (projectId) =>
      getters.messages(projectId).find((m) => m.id === state.activeThreadId) || null,
    threadForFeature: (state, getters) => (projectId, featureCode) =>
      getters.messages(projectId).find((m) => m.featureCode === featureCode) || null,
  },
  mutations: {
    SET_ENABLED(state, { projectId, enabled }) {
      state.enabledByProject[String(projectId)] = enabled
    },
    SET_PIN_DROP(state, value) {
      state.pinDrop = value || null
    },
    SET_DRAFT_LOCATION(state, location) {
      state.draftLocation = location
    },
    // Accepts [lng, lat] or { location, geometry }; null clears both.
    SET_MAP_DRAFT(state, payload) {
      state.mapDraft = Array.isArray(payload) ? payload : payload?.location || null
      state.mapDraftGeometry = (payload && !Array.isArray(payload) && payload.geometry) || null
    },
    SET_ACTIVE_THREAD(state, messageId) {
      state.activeThreadId = messageId
    },
  },
  actions: {
    // Resolve the project's conversation and load its messages.
    async ensureLoaded({ dispatch }, projectId) {
      if (!projectId) return
      try {
        await dispatch('chat/ensureProjectConversation', projectId, { root: true })
      } catch (e) {
        // Not a member / project gone — the feed just stays empty.
      }
    },
    async sendMessage(
      { state, commit, dispatch, rootGetters },
      { projectId, text, mentions, attachments, location, geometry, featureCode }
    ) {
      const uuid = await dispatch('chat/ensureProjectConversation', projectId, { root: true })
      const message = await dispatch(
        'chat/sendMessage',
        {
          uuid,
          text,
          mentions,
          attachments,
          location: location ?? state.draftLocation,
          geometry: geometry ?? (featureCode ? null : state.mapDraftGeometry),
          featureCode,
        },
        { root: true }
      )
      commit('SET_DRAFT_LOCATION', null)
      return message
    },
    async sendReply({ dispatch, rootGetters }, { projectId, messageId, text, mentions, attachments }) {
      const uuid = rootGetters['chat/projectUuid'](projectId)
      if (!uuid) return null
      return dispatch(
        'chat/sendMessage',
        { uuid, text, mentions, attachments, parentId: messageId },
        { root: true }
      )
    },
    // Agent replies are triggered server-side; this only shows the typing
    // indicator until the reply lands over WS.
    notePendingAgent({ dispatch, rootGetters }, { projectId, name }) {
      const uuid = rootGetters['chat/projectUuid'](projectId)
      if (!uuid) return
      dispatch('chat/notePendingAgent', { uuid, name }, { root: true })
    },
  },
}
